import { useState, useEffect, useMemo, useRef } from "react";
import { useCoursesLoaderData, useProgramDetails } from "./coursesLoader.ts";
import { getTermMarketPrice } from "common/api/decimal/methods/course.ts";
import usePvsPricingDiagnostics, {
  MarketPriceLineItem,
} from "./usePvsPricingDiagnostics.ts";

interface ProgramPricing {
  price: number | null;
}

export default function useProgramPricing(): {
  ea: ProgramPricing & { totalCreditHours: number | null };
  ia: ProgramPricing;
  np: ProgramPricing & { waiting: boolean };
} {
  const { currentTerm, learnerRequiredMaterials } = useCoursesLoaderData();
  const {
    np: { showPVSActualMarketPrice },
    personalizedValueSheet: { showPriceDrawer, showPricing },
    scope,
  } = useProgramDetails();

  let requireEAPrice = false;
  let requireIAPrice = false;
  if (scope === "flexible") {
    requireEAPrice = true;
    requireIAPrice = true;
  } else if (scope === "ea_only") {
    requireEAPrice = true;
  } else if (scope === "ia_only") {
    requireIAPrice = true;
  }

  const eaPrice = currentTerm?.equitableAccessExtras?.price ?? null;
  const eaTotalCreditHours =
    currentTerm?.equitableAccessExtras?.totalCreditHours ?? null;

  const requiredMaterialPrices = learnerRequiredMaterials
    .map((m) => m.studentPrice)
    .filter((price): price is number => price !== null && !isNaN(Number(price)))
    .map(Number);

  const iaPrice =
    requiredMaterialPrices.length > 0
      ? requiredMaterialPrices.reduce((sum, price) => sum + price, 0)
      : null;

  const hasRequiredPricing =
    (requireEAPrice === false || (requireEAPrice && eaPrice !== null)) &&
    (requireIAPrice === false || (requireIAPrice && iaPrice !== null));

  const requiredIdentifiers = useMemo(
    () => learnerRequiredMaterials.map((m) => [m.isbn, m.vbid]).filter(Boolean),
    [learnerRequiredMaterials]
  );

  const shouldFetch =
    (showPricing || showPriceDrawer) &&
    showPVSActualMarketPrice &&
    hasRequiredPricing &&
    requiredIdentifiers.length > 0;

  const [npPrice, setNpPrice] = useState<number | null>(null);
  const [npWaiting, setNpWaiting] = useState<boolean>(shouldFetch);
  // This state shows the difference between a failed request and an empty
  // price. Only the checklist below uses it. It is not part of the public
  // result of this hook.
  const [npError, setNpError] = useState<boolean>(false);
  // Ref (not state) because it only feeds the diagnostics checklist, not the UI.
  const npFailedLineItemsRef = useRef<MarketPriceLineItem[]>([]);

  useEffect(() => {
    if (!shouldFetch) {
      setNpWaiting(false);
      return;
    }

    setNpWaiting(true);
    setNpError(false);
    npFailedLineItemsRef.current = [];
    getTermMarketPrice(requiredIdentifiers)
      .then((result) => {
        if (result.ok && result.data?.totalPrice != null) {
          setNpPrice(result.data.totalPrice);
        } else if (!result.ok) {
          setNpError(true);
        } else {
          npFailedLineItemsRef.current = (result.data?.lineItems ?? []).filter(
            (item) => item.price === null
          );
        }
      })
      .catch(() => {
        setNpError(true);
      })
      .finally(() => {
        setNpWaiting(false);
      });
  }, [shouldFetch, requiredIdentifiers]);

  usePvsPricingDiagnostics({
    showPricing,
    showPriceDrawer,
    showPVSActualMarketPrice,
    hasRequiredPricing,
    eaPrice,
    iaPrice,
    requiredMaterialCount: learnerRequiredMaterials.length,
    // Count the materials here. Do not use requiredIdentifiers.length,
    // because that value is always equal to learnerRequiredMaterials.length.
    // The filter above has no effect.
    identifierCount: learnerRequiredMaterials.filter((m) => m.isbn || m.vbid)
      .length,
    shouldFetch,
    npPrice,
    npWaiting,
    npError,
    npFailedLineItems: npFailedLineItemsRef.current,
  });

  return {
    ea: {
      price: eaPrice,
      totalCreditHours: eaTotalCreditHours,
    },
    ia: {
      price: iaPrice,
    },
    np: {
      price: npPrice,
      waiting: npWaiting,
    },
  };
}
